// KanbanBoard.jsx
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Row, Col } from "react-bootstrap";
import PostItem from "./PostItem";

/**
 * Option A: Controlled Kanban (data in, events out)
 *
 * - Parent owns items and canonical state.
 * - KanbanBoard renders columns + handles DnD UI.
 * - On drop it emits an event with ordering context (beforeId/afterId).
 *
 * Assumptions:
 * - columns[columnId].items is an array of "items"
 * - getId(item) returns stable string/number id
 */
export default function KanbanBoard({
  columns, 
  getId, // (item) => string|number
  renderItem, // ({ item, columnId, index }) => ReactNode
  canMove, // ({ item, fromColumnId, toColumnId, fromIndex, toIndex }) => boolean (optional)
  onMove, // async ({ item, fromColumnId, toColumnId, fromIndex, toIndex, beforeId, afterId }) => void
  onReorder, // async ({ item, columnId, fromIndex, toIndex, beforeId, afterId }) => void (optional)
  columnWidthPct = 20,
  columnMaxWidthPx = 240,
  gapPx = 20,
  emptyDropMessage, // optional string
  dragDisabled = false, // read-only board: cards render but cannot be picked up
}) {
  const getBeforeAfter = (items, destinationIndex) => {
    const safeId = (it) => (it ? String(getId(it)) : null);
    const before =
      destinationIndex - 1 >= 0 ? items[destinationIndex - 1] : null;
    const after =
      destinationIndex + 1 < items?.length ? items[destinationIndex + 1] : null;

    return {
      beforeId: safeId(before),
      afterId: safeId(after),
    };
  };

  const onDragEnd = async (result) => {
    const { source, destination } = result;

    if (!destination) {
      if (emptyDropMessage) alert(emptyDropMessage);
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const fromColumnId = source.droppableId;
    const toColumnId = destination.droppableId;

    const fromCol = columns[fromColumnId];
    const toCol = columns[toColumnId];

    if (!fromCol || !toCol) return;

    const item = fromCol.items[source.index];
    if (!item) return;

    const allowed =
      typeof canMove === "function"
        ? canMove({
            item,
            fromColumnId,
            toColumnId,
            fromIndex: source.index,
            toIndex: destination.index,
          })
        : true;

    if (!allowed) return;

    // Build "what the destination list will look like" to compute before/after ids
    let destPreview = [...toCol.items];
    if (fromColumnId === toColumnId) {
      // reorder within same column
      const copy = [...fromCol.items];
      const [moved] = copy.splice(source.index, 1);
      copy.splice(destination.index, 0, moved);
      destPreview = copy;
    } else {
      // move across columns
      destPreview = [...toCol.items];
      destPreview.splice(destination.index, 0, item);
    }

    const { beforeId, afterId } = getBeforeAfter(
      destPreview,
      destination.index,
    );

    // Emit events to parent (parent updates canonical state)
    if (fromColumnId === toColumnId) {
      if (typeof onReorder === "function") {
        await onReorder({
          item,
          columnId: fromColumnId,
          fromIndex: source.index,
          toIndex: destination.index,
          beforeId,
          afterId,
        });
      } else {
        // fallback: treat reorder as move within same column
        await onMove({
          item,
          fromColumnId,
          toColumnId,
          fromIndex: source.index,
          toIndex: destination.index,
          beforeId,
          afterId,
        });
      }
      return;
    }

    await onMove({
      item,
      fromColumnId,
      toColumnId,
      fromIndex: source.index,
      toIndex: destination.index,
      beforeId,
      afterId,
    });
  };
  return (
    <div className="task-board">
      <DragDropContext onDragEnd={onDragEnd}>
        <div style={{ display: "flex", gap: `${gapPx}px` }}>
          <Row className="w-100 flex-fill">
            {Object.entries(columns).map(([columnId, column]) => (
              <Droppable key={columnId} droppableId={columnId}>
                {(provided) => (
                  <Col
                    style={{
                      flex: `0 0 ${columnWidthPct}%`,
                      maxWidth: `${columnWidthPct}%`,
                    }}
                  >
                    <div className="task-list">
                      <div className="card bg-light shadow-none h-100">
                        <div className="card-header bg-transparent border-bottom d-flex align-items-start">
                          <div className="flex-grow-1">
                            <h4 className="card-title mb-0">
                              {column.name} &nbsp;
                              <span className="ml-5 text-muted">
                                ({column.items?.length})
                              </span>
                            </h4>
                          </div>
                        </div>

                        <div className="tasklist-content p-3 simplebar-scrollable-y">
                          <div className="simplebar-wrapper">
                            <div
                              className="tasks"
                              style={{ maxWidth: `${columnMaxWidthPx}px` }}
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {column.items.map((item, index) => (
                                <Draggable
                                  key={String(getId(item))}
                                  draggableId={String(getId(item))}
                                  index={index}
                                  isDragDisabled={dragDisabled}
                                >
                                  {(dragProvided) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                    >
                                      {renderItem({ item, columnId, index })}
                                    </div>
                                  )}
                                </Draggable>
                              ))}

                              {provided.placeholder}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                )}
              </Droppable>
            ))}
          </Row>
        </div>
      </DragDropContext>
    </div>
  );
}
